import React, { useEffect, useRef, useState, ReactNode, ReactElement } from 'react'

// import type {MenuTriggerProps} from 'react-stately';
import { useMenuTrigger, useMenu, useMenuItem } from 'react-aria';
import { Item, useMenuTriggerState, useTreeState, ItemProps } from 'react-stately';

// Reuse the Popover, and Button from your component library. See below for details.
import { Popover } from 'bwax-ui/components/PopoverTrigger';
import { Pressable } from 'bwax-ui/components/Button';

import styleClasses from './DropdownMenu.module.css';
import clsx from 'clsx';

// 定义各种接口
interface MenuItemType {

    label: string;
    icon?: ReactNode;
    isDisabled?: boolean;
    onSelect?: () => void;
    subItems?: MenuItemType[];
    hasUpdates?: boolean;
    type?: "separator" | "header" | "item";
    color?: string;
}

interface DropdownMenuProps {
    items: MenuItemType[];
    className?: string;
    children: ReactNode;
    placement?: string;
    itemClassName?: string;
    zIndex?: number;
    onOpenChange?: (isOpen: boolean) => void;
}

interface DropdownMenuTriggerProps {
    trigger: ReactNode;
    placement?: string;
    isOpen?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    className?: string;
    zIndex?: number;
    children: ReactNode;
    disabledKeys?: any[];
    onAction?: (index: number) => void;
}

interface MenuProps {
    className?: string;
    sub?: boolean;
    onFocusChange?: (isFocused: boolean) => void;
    close: () => void;
    [key: string]: any;
}

interface MenuItemProps {
    item: any;
    state: any;
    onFocusChange?: (key: any, isFocused: boolean) => void;
    close: () => void;
}

interface SubMenuProps {
    items: MenuItemType[];
    onFocusChange: (isFocused: boolean) => void;
    close: () => void;
    isOpen?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    triggerRef: React.RefObject<any>;
}

/**
'bottom'
  | 'bottom left'
  | 'bottom right'
  | 'bottom start'
  | 'bottom end'
  | 'top'
  | 'top left'
  | 'top right'
  | 'top start'
  | 'top end'
  | 'left'
  | 'left top'
  | 'left bottom'
  | 'start'
  | 'start top'
  | 'start bottom'
  | 'right'
  | 'right top'
  | 'right bottom'
  | 'end'
  | 'end top'
  | 'end bottom'
 */

export default function DropdownMenu({ items: givenItems, className, children, placement = "bottom start", itemClassName, zIndex, onOpenChange }: DropdownMenuProps) {

    const items = givenItems.filter(item => !!item);

    return (
        <DropdownMenuTrigger onOpenChange={onOpenChange} zIndex={zIndex} trigger={children} placement={placement} className={className} onAction={index => {
            const item = items[index];
            if (item && item.onSelect && !item.isDisabled) {
                item.onSelect();
            }
        }}>
            {items.map((item, index) => {
                const {
                    label, icon, isDisabled, onSelect, subItems, hasUpdates, color,
                } = item

                return (
                    // @ts-expect-error isDisabled实际上在运行时有效，但TypeScript类型定义中未包含此属性
                    <Item key={index} textValue={label || "--"} isDisabled={isDisabled} type={item.type} color={color}>
                        {icon ? (
                            <div className={clsx("flex w-6 justify-center relative")}>
                                {icon}
                                {hasUpdates ?
                                    <div className="absolute rounded-[50%] bg-[var(--tomato-9)] h-1.5 w-1.5 right-[-2px] top-[-1px]" /> : null
                                }
                            </div>
                        ) : null}
                        {label}
                    </Item>
                )
            })}
        </DropdownMenuTrigger>
    )
}

export function DropdownMenuTrigger(props: DropdownMenuTriggerProps) {
    // Create state based on the incoming props

    const {
        trigger, placement,
        isOpen, defaultOpen, onOpenChange,
        className, zIndex,
    } = props;

    const state = useMenuTriggerState({ isOpen, defaultOpen, onOpenChange });

    // Get props for the button and menu elements
    const ref = useRef(null);
    const { menuTriggerProps, menuProps } = useMenuTrigger({}, state, ref);

    return (
        <>
            <Pressable
                {...menuTriggerProps}
                ref={ref}
                style={{ height: 30, fontSize: 14 }}
            >
                {React.isValidElement(trigger) ? trigger : <span>{String(trigger || '')}</span>}
            </Pressable>
            {state.isOpen ? (
                // @ts-ignore 忽略 Popover 组件类型错误
                <Popover state={state} triggerRef={ref} placement={placement} withArrow={false} zIndex={zIndex} className={className || ""}>
                    <Menu
                        {...props}
                        {...menuProps}
                        className={className}
                        close={state.close}
                    />
                </Popover>
            ) : null}
        </>
    );
}

function Menu({ className = "font-size-13", sub = false, onFocusChange = (_: boolean) => { }, close, ...props }: MenuProps) {
    // Create menu state based on the incoming props
    const state = useTreeState(props);

    // Get props for the menu element
    const ref = React.useRef(null);
    const { menuProps } = useMenu(props, state, ref);

    const [focusState, setFocusState] = useState<Record<string, boolean>>({});

    const checkFocus = (focusState: Record<string, boolean>) => Object.keys(focusState).some(k => focusState[k]);

    const focusStateRef = useRef(focusState);
    focusStateRef.current = focusState;

    const onItemFocusChange = (key: string | number, isFocused: boolean) => {
        const isFocusedBefore = checkFocus(focusStateRef.current);

        const newFocusState = {
            ...focusStateRef.current,
            [key]: isFocused,
        }

        const isFocusedNow = checkFocus(newFocusState);

        focusStateRef.current = newFocusState;
        // if (isFocusedNow !== isFocusedBefore) {

        // }
        onFocusChange(isFocusedNow)
        setFocusState(newFocusState)
    };

    return (
        <ul
            {...menuProps}
            ref={ref}
            className={clsx(className, styleClasses["lc-dropdown-menu"])}
            style={{
            }}
        >
            {Array.from(state.collection).map((item) => (
                // item.type === 'section'
                //     ? <MenuSection key={item.key} section={item} state={state} />
                //     : 
                <MenuItem key={item.key} item={item} state={state} onFocusChange={onItemFocusChange} close={close} />
            ))}
        </ul>
    );
}


function MenuItem({ item, state, onFocusChange = (_: any, __: boolean) => { }, close }: MenuItemProps) {
    // Get props for the menu item element
    let ref = React.useRef(null);
    const allMenuItemProps = useMenuItem(
        {
            key: item.key,
            ...item.props,
        },
        state,
        ref
    );

    const { menuItemProps, isFocused, isSelected, isDisabled } = allMenuItemProps;
    const { subItems } = item.props;

    useEffect(() => {
        onFocusChange(item.key, isFocused);

        if (subItems && isFocused) {
            setShouldSubMenuOpen(true);
        }
    }, [isFocused]);


    const [isSubMenuFocused, setIsMenuFocused] = useState(false);

    const [shouldSubMenuOpen, setShouldSubMenuOpen] = useState(false);

    function onSubMenuFocusChange(isSubMenuFocused: boolean) {
        setIsMenuFocused(isSubMenuFocused);
    }

    const { onClick, onPointerUp, ...otherMenuItemProps } = menuItemProps;

    if (item.props.type === "separator") {
        return (
            <li className="h-[1px] bg-[var(--gray-3)] my-2.5 !py-0" />
        )
    }

    return (
        <>
            <li
                {...otherMenuItemProps}
                data-focused={isFocused || (isSubMenuFocused && shouldSubMenuOpen)}
                data-disabled={isDisabled}
                data-selected={isSelected}
                ref={ref}
                className={clsx("justify-between cursor-pointer", item.props.className)}
                onClick={e => {
                    if (subItems) {
                        // do nothing
                    } else if (onClick) {
                        onClick(e);
                    }
                }}
                data-color={item.props.color}
                onPointerUp={e => {
                    if (subItems) {
                        // do nothing
                    } else if (onPointerUp) {
                        onPointerUp(e);
                    }
                }}
            >
                <div className="flex gap-2 items-center">
                    {item.rendered}
                </div>
                {subItems ?
                    <i className='bx bx-chevron-right'></i> : null
                }
            </li>
            {/* {} */}
            {subItems ?
                // @ts-ignore 忽略 SubMenu 类型错误
                <SubMenu
                    isOpen={(isFocused || isSubMenuFocused) && shouldSubMenuOpen} triggerRef={ref} items={subItems} onFocusChange={onSubMenuFocusChange}
                    onOpenChange={(open: boolean) => {
                        if (!open) {
                            setShouldSubMenuOpen(false);
                        }
                    }}
                    close={() => {
                        close();
                    }}
                /> : null
            }
        </>
    );
}

function SubMenu(props: SubMenuProps) {

    const {
        items, onFocusChange, close,
        isOpen, defaultOpen, onOpenChange, triggerRef,
    } = props;

    const state = useMenuTriggerState({ isOpen, defaultOpen, onOpenChange });

    return (
        state.isOpen ? (
            // @ts-ignore 忽略 Popover 组件类型错误
            <Popover state={state} triggerRef={triggerRef} placement={"end"} withArrow={false} className="" zIndex={1000}>
                <Menu aria-label={"Sub Menu"} onFocusChange={onFocusChange} sub={true} close={close}
                    disabledKeys={items.reduce((acc, item, idx) => item.isDisabled ? [...acc, idx] : acc, [] as number[])}
                    onAction={(index: number) => {
                        const item = items[index];
                        if (item && item.onSelect) {
                            item.onSelect();
                            close();
                        }
                    }}>
                    {items.map((item, index) => {
                        const { label, icon, isDisabled, onSelect, subItems } = item;
                        return (
                            // @ts-expect-error isDisabled实际上在运行时有效，但TypeScript类型定义中未包含此属性
                            <Item key={index} textValue={label} isDisabled={isDisabled}>
                                {icon ? icon : null} {label}
                            </Item>
                        )
                    })}
                </Menu>
            </Popover>
        ) : null
    )
}